import { useEffect, useState, type RefObject } from 'react';

import { readCssDurationMs } from './readCssDurationMs';

/**
 * Keeps a block mounted while it animates closed, so collapsing a section plays
 * its exit instead of the content vanishing on the next paint. The exit
 * duration comes from the stylesheet, which owns the timing: it is read from the
 * custom property named by `durationProperty` on the element.
 */
export function useRevealPresence(
  isOpen: boolean,
  elementRef: RefObject<HTMLElement | null>,
  durationProperty: string,
) {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLeaving(false);
      setIsRendered(true);
      return;
    }
    if (!isRendered) {
      return;
    }

    setIsLeaving(true);
    const timeout = setTimeout(
      () => {
        setIsLeaving(false);
        setIsRendered(false);
      },
      readCssDurationMs(elementRef.current, durationProperty),
    );
    return () => clearTimeout(timeout);
  }, [durationProperty, elementRef, isOpen, isRendered]);

  return { isLeaving, isRendered };
}
